import React, { useId } from 'react';

import type {
  CircularProgressGeometry,
  CircularProgressProps,
  ProgressSize,
  ProgressVariant,
} from './Progress.types';

/**
 * Size configuration for CircularProgress
 */
const sizeConfig: Record<
  ProgressSize,
  { size: number; strokeWidth: number; labelSize: string }
> = {
  sm: { size: 24, strokeWidth: 2, labelSize: 'text-label-xs' },
  md: { size: 40, strokeWidth: 3, labelSize: 'text-label-sm' },
  lg: { size: 64, strokeWidth: 4, labelSize: 'text-body-md' },
};

/**
 * Variant colors for CircularProgress (using Tailwind stroke classes)
 */
const variantConfig: Record<ProgressVariant, { stroke: string }> = {
  // primary-400 (#e879f9) is the canonical accent stroke — it is what
  // `--color-stroke-accent-strong` resolves to.
  primary: { stroke: 'stroke-primary-400' },
  contrast: { stroke: 'stroke-contrast-500' },
  secondary: { stroke: 'stroke-secondary-500' },
  success: { stroke: 'stroke-success-500' },
  error: { stroke: 'stroke-error-500' },
  warning: { stroke: 'stroke-warning-500' },
  neutral: { stroke: 'stroke-neutral-400' },
};

/**
 * CircularProgress component for showing progress as a circular indicator.
 * Supports determinate (percentage) and indeterminate modes.
 */
const CircularProgress = React.forwardRef<
  HTMLDivElement,
  CircularProgressProps
>(
  (
    {
      value,
      max = 100,
      variant = 'contrast',
      size = 'md',
      showLabel = false,
      label,
      strokeWidth: customStrokeWidth,
      trackClassName = 'stroke-contrast-700',
      fill = 'solid',
      children,
      overlay,
      'aria-label': ariaLabel,
      role = 'progressbar',
      className = '',
      ...props
    },
    ref
  ) => {
    const gradientId = useId();
    // Determine if indeterminate mode (no value provided)
    const isIndeterminate = value === undefined;

    // Calculate percentage for determinate mode
    const percentage = isIndeterminate
      ? 0
      : Math.min(100, Math.max(0, (value / max) * 100));

    // A number is an explicit diameter; a named step reads its own config.
    const config =
      typeof size === 'number'
        ? { size, strokeWidth: 4, labelSize: 'text-body-md' }
        : sizeConfig[size];
    const variantStyles = variantConfig[variant];
    const strokeWidth = customStrokeWidth ?? config.strokeWidth;

    // SVG calculations
    const svgSize = config.size;
    const radius = (svgSize - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = isIndeterminate
      ? circumference * 0.75
      : circumference - (percentage / 100) * circumference;

    // Label to display
    const displayLabel = label ?? `${Math.round(percentage)}%`;

    const geometry: CircularProgressGeometry = {
      center: svgSize / 2,
      radius,
      circumference,
    };

    // Container classes
    const containerClasses = [
      'relative inline-flex items-center justify-center',
      className,
    ].join(' ');

    // Progress circle classes
    const progressClasses = [
      fill === 'solid' ? variantStyles.stroke : '',
      'transition-all duration-slow ease-out',
      isIndeterminate ? 'animate-circular-progress-spin' : '',
    ].join(' ');

    return (
      <div
        ref={ref}
        role={role}
        aria-label={ariaLabel}
        // A caller can name the ring another role — a score is an image of a
        // number, not a task's progress — and then the value attributes go.
        {...(role === 'progressbar'
          ? {
              'aria-valuenow': isIndeterminate ? undefined : percentage,
              'aria-valuemin': 0,
              'aria-valuemax': 100,
            }
          : {})}
        className={containerClasses}
        {...props}
      >
        <svg
          width={svgSize}
          height={svgSize}
          viewBox={`0 0 ${svgSize} ${svgSize}`}
          className={isIndeterminate ? 'animate-spin' : ''}
          style={{ transform: 'rotate(-90deg)' }}
        >
          {fill === 'bloom' && (
            <defs>
              <linearGradient
                id={gradientId}
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="var(--color-primary-300)" />
                <stop offset="100%" stopColor="var(--color-secondary-300)" />
              </linearGradient>
            </defs>
          )}
          {/* Track circle */}
          <circle
            cx={svgSize / 2}
            cy={svgSize / 2}
            r={radius}
            fill="none"
            strokeWidth={strokeWidth}
            className={trackClassName}
          />
          {/* Caller's own mark, on the same geometry, under the arc */}
          {overlay?.(geometry)}

          {/* Progress circle */}
          <circle
            cx={svgSize / 2}
            cy={svgSize / 2}
            r={radius}
            fill="none"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            stroke={fill === 'bloom' ? `url(#${gradientId})` : undefined}
            className={progressClasses}
          />
        </svg>

        {/* Centre: a caller's own content wins over the percentage label. */}
        {children ? (
          <div className="absolute inset-0 flex items-center justify-center">
            {children}
          </div>
        ) : (
          showLabel &&
          !isIndeterminate && (
            <span
              className={`absolute inset-0 flex items-center justify-center font-medium text-neutral-50 ${config.labelSize}`}
              style={{ transform: 'rotate(0deg)' }}
            >
              {displayLabel}
            </span>
          )
        )}
      </div>
    );
  }
);

CircularProgress.displayName = 'CircularProgress';

export { CircularProgress };
export default CircularProgress;
